import {
  MessageTemplateCategoryDto,
  MustacheTemplateVariablesDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Button } from '@act/storybook';
import { Fragment, useEffect, useState } from 'react';
import Loader from '@act/react-components/workflows/Loader';
import { messagesRequests } from 'redux/requests/messages.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import SuggestedTemplatesDisplay from './SuggestedTemplatesDisplay';
import TemplatesDisplay from './TemplatesDisplay';
import { useTracking } from 'react-components/tracking';
import { StageKey } from '@act/shared/definitions';
import './message-templates.scss';

interface MessageTemplatesProps {
  handleApplyTemplate: (
    templateString: string,
    templateVariables: MustacheTemplateVariablesDto
  ) => void;
  user: UserDto;
}

const MessageTemplates = ({
  handleApplyTemplate,
  user,
}: MessageTemplatesProps) => {
  const { generateTrackingProps } = useTracking();
  const [
    templateCategoriesFromEndpoint,
    isLoadingFromEndpoint,
    getMessageTemplates,
  ] = useApiCall(messagesRequests.getMessageTemplates);
  const [templateCategories, setTemplateCategories] = useState<
    MessageTemplateCategoryDto[]
  >([]);
  const [templateVariables, isVariablesLoading, getTemplateVariables] =
    useApiCall(messagesRequests.getTemplateVariables);

  const [selectedTemplateCategory, setSelectedTemplateCategory] =
    useState(null);

  useEffect(() => {
    if (user) {
      void getMessageTemplates({ params: { userId: user.id } });
      void getTemplateVariables({ params: { userId: user.id } });
    }
  }, [user]);

  useEffect(() => {
    if (templateCategoriesFromEndpoint) {
      const canUserSuggestedTextMessageLLM =
        user.memberStage?.key !== 'independence';

      let templateCategories: MessageTemplateCategoryDto[] =
        templateCategoriesFromEndpoint;

      if (canUserSuggestedTextMessageLLM) {
        templateCategories = [
          {
            id: -1,
            name: 'Suggested',
            templates: [],
            sortOrder: 0,
            stageKey: '' as StageKey,
          },
          ...templateCategoriesFromEndpoint,
        ];
      }

      setTemplateCategories(templateCategories);
    }
  }, [templateCategoriesFromEndpoint, user]);

  if (isLoadingFromEndpoint || isVariablesLoading) {
    return <Loader />;
  } else {
    return (
      !!templateCategories?.length &&
      templateVariables && (
        <div className="message-templates">
          <h4>
            {!selectedTemplateCategory && 'Message Templates'}
            {selectedTemplateCategory && (
              <span>
                <Button
                  className="navigation-button"
                  size="small"
                  onClick={() => setSelectedTemplateCategory(null)}
                  {...generateTrackingProps('template-back-button')}
                >
                  Message Templates
                </Button>{' '}
                / {selectedTemplateCategory.name}
              </span>
            )}
          </h4>
          <div className="buttons-wrapper">
            {templateCategories.map(t => (
              <Fragment key={t.name}>
                {!selectedTemplateCategory && (
                  <Button
                    className="category-button"
                    size="small"
                    onClick={() => setSelectedTemplateCategory(t)}
                    trackingName={`template-category-${t.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}-button`}
                  >
                    {t.name}
                  </Button>
                )}
                {selectedTemplateCategory?.id === t.id &&
                  (t.name === 'Suggested' ? (
                    <SuggestedTemplatesDisplay
                      handleApplyTemplate={handleApplyTemplate}
                      templateVariables={templateVariables}
                      memberId={user?.id}
                    />
                  ) : (
                    <TemplatesDisplay
                      templates={t.templates}
                      handleApplyTemplate={handleApplyTemplate}
                      templateVariables={templateVariables}
                    />
                  ))}
              </Fragment>
            ))}
          </div>
        </div>
      )
    );
  }
};

export default MessageTemplates;
